import apiClient from './api';
import type { User } from '@/types';

export interface CreateUserDTO {
  email: string;
  nom: string;
  prenom: string;
  password: string;
  roles?: string[];
  telephone?: string;
  actif?: boolean;
  // Compagnies visibles (ROLE_COMPAGNIE uniquement)
  compagnieIds?: number[];
  // Autorise ROLE_COMPAGNIE a creer une demande de deneigement (optionnel, defaut false)
  autoriseCreationIntervention?: boolean;
}

export interface UpdateUserDTO {
  email?: string;
  nom?: string;
  prenom?: string;
  password?: string;
  roles?: string[];
  telephone?: string;
  actif?: boolean;
  compagnieIds?: number[];
  autoriseCreationIntervention?: boolean;
}

export interface UserFilters {
  role?: string;
  actif?: boolean;
  search?: string;
}

export const userService = {
  /**
   * Liste des utilisateurs avec filtres
   */
  async list(filters: UserFilters = {}): Promise<User[]> {
    const params = new URLSearchParams();

    if (filters.role) params.append('role', filters.role);
    if (filters.actif !== undefined) params.append('actif', String(filters.actif));
    if (filters.search) params.append('search', filters.search);

    const response = await apiClient.get<User[]>(`/users?${params.toString()}`);
    return response.data;
  },

  /**
   * Detail d'un utilisateur
   */
  async get(id: number): Promise<User> {
    const response = await apiClient.get<User>(`/users/${id}`);
    return response.data;
  },

  /**
   * Creation d'un utilisateur
   */
  async create(data: CreateUserDTO): Promise<User> {
    const response = await apiClient.post<User>('/users', data);
    return response.data;
  },

  /**
   * Modification d'un utilisateur
   */
  async update(id: number, data: UpdateUserDTO): Promise<User> {
    const response = await apiClient.put<User>(`/users/${id}`, data);
    return response.data;
  },

  /**
   * Suppression (desactivation) d'un utilisateur
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },

  /**
   * Changement de mot de passe
   */
  async changePassword(id: number, password: string): Promise<void> {
    await apiClient.patch(`/users/${id}/password`, { password });
  },

  /**
   * Activation/Desactivation
   */
  async toggleActif(id: number): Promise<{ actif: boolean }> {
    const response = await apiClient.patch<{ id: number; actif: boolean }>(`/users/${id}/toggle-actif`);
    return { actif: response.data.actif };
  },

  /**
   * Reinitialisation de la 2FA par un administrateur
   */
  async reset2FA(userId: number): Promise<void> {
    await apiClient.delete(`/users/${userId}/2fa`);
  },

  /**
   * Liste des roles disponibles
   */
  async getRoles(): Promise<Record<string, string>> {
    const response = await apiClient.get<Record<string, string>>('/users/roles/list');
    return response.data;
  },

  /**
   * Import d'agents depuis un fichier Excel
   */
  async importFromFile(
    file: File,
    role: string = 'ROLE_AGENT_DENEIGNEMENT',
    defaultPassword: string = 'agent123'
  ): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('role', role);
    formData.append('defaultPassword', defaultPassword);

    const response = await apiClient.post<ImportResult>('/users/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Telecharge le modele d'import
   */
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get('/users/import/template', {
      responseType: 'blob',
    });
    return response.data;
  },
};

export interface ImportResult {
  success: boolean;
  imported: number;
  skipped: number;
  errors: string[];
  message: string;
}

export default userService;
